import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast, toast } from '../components/UI/Toast';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import { ApiService } from '../services/api';
import { Search, CheckCircle, Clock, XCircle, AlertTriangle, Loader2, ArrowLeft } from 'lucide-react';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 3rem;
`;

const BackButton = styled.button<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1.5rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const Header = styled.div`
  text-align: center;
`;

const Title = styled.h1<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.125rem;
  line-height: 1.6;
  margin: 0;
`;

const SearchSection = styled.div`
  margin-bottom: 2rem;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input<{ theme: any; hasError?: boolean }>`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.hasError ? props.theme.colors.error : props.theme.colors.border};
  border-radius: 8px;
  font-size: 1rem;
  font-family: 'Raleway', sans-serif;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const SearchButton = styled(Button)<{ isLoading: boolean }>`
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  ${props => props.isLoading && `
    pointer-events: none;
    opacity: 0.7;
  `}
`;

const SpinningLoader = styled(Loader2)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ResultSection = styled.div`
  margin-top: 2rem;
`;

const StatusCard = styled(Card)<{ status: string; theme: any }>`
  border-left: 4px solid ${props => {
    switch (props.status) {
      case 'pending': return '#f59e0b';
      case 'approved': return props.theme.colors.success;
      case 'rejected': return props.theme.colors.error;
      default: return props.theme.colors.border;
    }
  }};
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatusIcon = styled.div<{ status: string; theme: any }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.status) {
      case 'pending': return '#f59e0b20';
      case 'approved': return props.theme.colors.success + '20';
      case 'rejected': return props.theme.colors.error + '20';
      default: return props.theme.colors.border + '20';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#f59e0b';
      case 'approved': return props.theme.colors.success;
      case 'rejected': return props.theme.colors.error;
      default: return props.theme.colors.text;
    }
  }};
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.h3<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

const StatusSubtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  margin: 0;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span<{ theme: any }>`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span<{ theme: any }>`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text};
  font-weight: 500;
`;

const TrackingId = styled.span<{ theme: any }>`
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: ${props => props.theme.colors.surface};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.colors.border};
`;

const NotFoundSection = styled.div<{ theme: any }>`
  text-align: center;
  padding: 3rem 2rem;
  background: ${props => props.theme.colors.surface};
  border: 2px dashed ${props => props.theme.colors.error}40;
  border-radius: 12px;
  margin-top: 2rem;
`;

const NotFoundIcon = styled.div<{ theme: any }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.theme.colors.error}20;
  color: ${props => props.theme.colors.error};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
`;

const NotFoundTitle = styled.h3<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const NotFoundMessage = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
`;

const NotFoundSuggestions = styled.ul<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
  padding-left: 1.5rem;
`;

const TrackingPage: React.FC = () => {
  const { theme } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [taxId, setTaxId] = useState(searchParams.get('taxId') || searchParams.get('id') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);

  usePageTitle('Track Application');

  const handleBack = () => {
    navigate('/');
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const cleanTaxId = taxId.replace(/\D/g, '');
    if (!cleanTaxId) {
      setError('Please enter your Tax ID');
      return;
    }

    if (!/^\d{13}$/.test(cleanTaxId)) {
      setError('Tax ID must be a 13-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);
    setIsNotFound(false);

    try {
      const response = await ApiService.trackRegistrationByTaxId(cleanTaxId);
      setResult(response);
      
      showToast(toast.success(
        'Registration Found',
        'Your tax consultant registration has been located successfully.'
      ));
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to track registration. Please try again.';

      if (err?.status === 404 || err?.payload?.error === 'not_found' || errorMessage === 'not_found') {
        setIsNotFound(true);
        setError(null);
        
        showToast(toast.error(
          'Not Found',
          'No registration was found for the Tax ID you entered. Please check and try again.'
        ));
      } else {
        setError(errorMessage);
        setIsNotFound(false);
        
        showToast(toast.error(
          'Tracking Failed',
          errorMessage
        ));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const normalizeStatus = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'APPROVED') return 'approved';
    if (s === 'REJECTED') return 'rejected';
    // DRAFT / SUBMITTED / everything else: pending
    return 'pending';
  };

  const getStatusIcon = (status: string) => {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'DRAFT':
      case 'SUBMITTED':
        return <Clock size={24} />;
      case 'APPROVED':
        return <CheckCircle size={24} />;
      case 'REJECTED':
        return <XCircle size={24} />;
      default:
        return <AlertTriangle size={24} />;
    }
  };

  const getStatusText = (status: string) => {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'DRAFT':
        return 'Draft';
      case 'SUBMITTED':
        return 'Submitted';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return 'Unknown Status';
    }
  };

  const getStatusDescription = (status: string) => {
    const s = (status || '').toUpperCase();
    switch (s) {
      case 'DRAFT':
        return 'Your registration is currently in draft state. Please complete and submit your registration.';
      case 'SUBMITTED':
        return 'Your registration has been submitted and is under review. You will be contacted once a decision is made.';
      case 'APPROVED':
        return 'Your registration has been approved. You can now login and access your dashboard.';
      case 'REJECTED':
        return 'Your registration was rejected. Please contact support for assistance.';
      default:
        return 'The status of your application is unclear. Please contact support for assistance.';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container>
      <HeaderSection>
        <BackButton theme={theme} onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to Home
        </BackButton>
        <Header>
          <Title theme={theme}>Track Your Application</Title>
          <Subtitle theme={theme}>
            Enter your Tax ID to check the status of your tax consultant registration application.
          </Subtitle>
        </Header>
      </HeaderSection>

      <Card>
        <SearchSection>
          <SearchForm onSubmit={handleSearch}>
            <SearchInput
              theme={theme}
              type="text"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value.replace(/\D/g, '').slice(0, 13))}
              placeholder="Enter Tax ID (13 digits) to track your registration"
              hasError={!!error}
              inputMode="numeric"
              maxLength={13}
            />
            <SearchButton
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <SpinningLoader size={16} />
                  Searching...
                </>
              ) : (
                <>
                  <Search size={16} />
                  Track Application
                </>
              )}
            </SearchButton>
          </SearchForm>
        </SearchSection>

        {isNotFound && (
          <ResultSection>
            <NotFoundSection theme={theme}>
              <NotFoundIcon theme={theme}>
                <XCircle size={32} />
              </NotFoundIcon>
              <NotFoundTitle theme={theme}>
                Registration Not Found
              </NotFoundTitle>
              <NotFoundMessage theme={theme}>
                No registration was found for the Tax ID you entered. This could happen for several reasons:
              </NotFoundMessage>
              <NotFoundSuggestions theme={theme}>
                <li>The Tax ID was entered incorrectly</li>
                <li>The registration is still being processed and not yet in the system</li>
                <li>The Tax ID must be 13 digits</li>
              </NotFoundSuggestions>
            </NotFoundSection>
          </ResultSection>
        )}

        {result && (
          <ResultSection>
            <StatusCard status={normalizeStatus(result.status)} theme={theme}>
              <StatusHeader>
                <StatusIcon status={normalizeStatus(result.status)} theme={theme}>
                  {getStatusIcon(result.status)}
                </StatusIcon>
                <StatusInfo>
                  <StatusTitle theme={theme}>
                    {getStatusText(result.status)}
                  </StatusTitle>
                  <StatusSubtitle theme={theme}>
                    {getStatusDescription(result.status)}
                  </StatusSubtitle>
                </StatusInfo>
              </StatusHeader>

              <DetailsGrid>
                <DetailItem>
                  <DetailLabel theme={theme}>Tax ID</DetailLabel>
                  <DetailValue theme={theme}>
                    <TrackingId theme={theme}>{result.taxId}</TrackingId>
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Practice</DetailLabel>
                  <DetailValue theme={theme}>{result.practice}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Name</DetailLabel>
                  <DetailValue theme={theme}>{result.name || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Submitted On</DetailLabel>
                  <DetailValue theme={theme}>{formatDate(result.createdAt)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Last Updated</DetailLabel>
                  <DetailValue theme={theme}>{formatDate(result.updatedAt)}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Email Address</DetailLabel>
                  <DetailValue theme={theme}>{result.email || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Phone</DetailLabel>
                  <DetailValue theme={theme}>{result.phone || '—'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel theme={theme}>Firm Name</DetailLabel>
                  <DetailValue theme={theme}>{result.firmName || '—'}</DetailValue>
                </DetailItem>
              </DetailsGrid>

            </StatusCard>
          </ResultSection>
        )}
      </Card>
    </Container>
  );
};

export default TrackingPage;
